import React from 'react';
import {Link} from 'react-router-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiAward,FiUsers,FiHeart,FiTrendingUp,FiStar,FiClock}=FiIcons;

const About=()=> {
  const stats=[
    {number: '500+',label: 'Events Decorated'},
    {number: '5+',label: 'Years Experience'},
    {number: '100%',label: 'Client Satisfaction'},
    {number: '24/7',label: 'Support Available'}
  ];

  const values=[
    {
      icon: FiHeart,
      title: 'Passion for Perfection',
      description: 'We pour our heart into every detail,ensuring each event is a masterpiece that exceeds expectations.'
    },
    {
      icon: FiAward,
      title: 'Quality Excellence',
      description: 'Using only premium materials and professional techniques to create decorations that stand out.'
    },
    {
      icon: FiUsers,
      title: 'Customer First',
      description: 'Your vision is our priority. We listen,understand,and bring your dreams to life with dedication.'
    },
    {
      icon: FiTrendingUp,
      title: 'Innovation & Creativity',
      description: 'Staying ahead of trends while creating unique,personalized designs that reflect your style.'
    }
  ];

  const team=[
    {
      name: 'Sarah Thompson',
      role: 'Creative Director',
      image: 'https://i.ibb.co/bMk3PMMN/Sarah-Thompson.jpg',
      description: 'With over 8 years in event design,Sarah leads our creative team with innovative vision.'
    },
    {
      name: 'Michael Chen',
      role: 'Senior Decorator',
      image: 'https://i.ibb.co/xSzLGdDL/Michael-Chen.jpg',
      description: 'Michael specializes in wedding decorations and has transformed over 200 celebrations.'
    },
    {
      name: 'Emily Rodriguez',
      role: 'Project Manager',
      image: 'https://i.ibb.co/Q30XHV18/Emily-Rodriguez.jpg',
      description: 'Emily ensures every project runs smoothly from consultation to final cleanup.'
    }
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-800 to-red-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{opacity: 0,x: -50}} animate={{opacity: 1,x: 0}} transition={{duration: 0.8}} >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">About Stars Event Decor</h1>
              <p className="text-xl text-red-100 mb-8 leading-relaxed">
                We are passionate decorators dedicated to transforming your special moments into 
                unforgettable experiences. Based in Scarborough,ON,we bring creativity, 
                professionalism,and attention to detail to every event we touch.
              </p>
              <Link to="/contact" className="bg-yellow-400 text-red-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors duration-200 inline-block">
                Work With Us
              </Link>
            </motion.div>
            <motion.div initial={{opacity: 0,x: 50}} animate={{opacity: 1,x: 0}} transition={{duration: 0.8,delay: 0.2}} >
              <img src="https://i.ibb.co/Cp933BqZ/wedding-image.jpg" alt="Stars Event Decor team at work" className="rounded-lg shadow-2xl w-full h-96 object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat,index)=> (
              <motion.div key={index} initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8,delay: index * 0.1}} viewport={{once: true}} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-red-800 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{opacity: 0,x: -50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.8}} viewport={{once: true}} >
              <img src="https://i.ibb.co/dJ0gX1bJ/birthday-image.jpg" alt="Our journey in event decoration" className="rounded-lg shadow-lg w-full h-96 object-cover" />
            </motion.div>
            <motion.div initial={{opacity: 0,x: 50}} whileInView={{opacity: 1,x: 0}} transition={{duration: 0.8,delay: 0.2}} viewport={{once: true}} className="space-y-6">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Our Story</h2>
              <p className="text-gray-600 text-lg leading-relaxed">
                Stars Event Decor began as a dream to make every celebration special. Founded in 2019, 
                we started with a simple belief: every event deserves to be magical,regardless of size or budget.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                What started as a small local business has grown into a trusted name in Scarborough and 
                surrounding areas. We've had the privilege of decorating hundreds of events,from intimate 
                birthday parties to grand weddings,each one unique and memorable.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                Our commitment to quality,creativity,and customer satisfaction has earned us the trust 
                of our community. We don't just decorate events;we create experiences that last a lifetime.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <motion.div initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Our Values</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              These core values guide everything we do and shape the way we serve our clients.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value,index)=> (
              <motion.div key={index} initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8,delay: index * 0.1}} viewport={{once: true}} className="text-center space-y-4">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <SafeIcon icon={value.icon} className="w-8 h-8 text-red-800" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Our talented team of designers and decorators brings creativity and expertise to every project.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {team.map((member,index)=> (
              <motion.div key={index} initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8,delay: index * 0.1}} viewport={{once: true}} className="bg-white rounded-lg shadow-lg overflow-hidden">
                <img src={member.image} alt={member.name} className="w-full h-64 object-cover" />
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-800 mb-1">{member.name}</h3>
                  <p className="text-red-800 font-medium mb-3">{member.role}</p>
                  <p className="text-gray-600">{member.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-red-800 text-white">
        <div className="container mx-auto px-4">
          <motion.div initial={{opacity: 0,y: 50}} whileInView={{opacity: 1,y: 0}} transition={{duration: 0.8}} viewport={{once: true}} className="text-center max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl text-red-100 mb-8 leading-relaxed">
              To transform ordinary spaces into extraordinary experiences through creative design, 
              exceptional service,and unwavering attention to detail. We believe every celebration 
              deserves to be special,and we're here to make that happen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services" className="bg-yellow-400 text-red-900 px-8 py-4 rounded-lg font-semibold hover:bg-yellow-300 transition-colors duration-200">
                View Our Services
              </Link>
              <Link to="/contact" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-red-800 transition-colors duration-200">
                Get In Touch
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;